import React, { useEffect } from 'react';
import { InputBaseComponentProps } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Icon from '@material-ui/core/Icon';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
// Components
import FormBoolean from './formBoolean';
import DateUtils from '../../utils/dateUtils';
import FormUtils from '../../utils/forms';
import ObjectUtils from '../../utils/objectUtils';
import FormTextArea from './formTextArea';
import FormInputList from './formInputList';
import FormOptions from './formOptions';
import FormInput from './formInput';
import FormSwitch from './formSwitch';
import FormChips from './formChips';
import FormSubmit from './formSubmit';
// Ressources
import { ComputeObject, DefaultFormData, FormDatas, InputDatas } from '../../local-types';
import { clsx } from '../../utils/htmlUtils';
import { trimObjectString } from '../../utils/businessUtils';
import { ACCEPTED_UPLOAD_TYPE } from '../../utils/constants';

interface FormProps extends WithTranslation {
    inputs: FormDatas[];
    payload: any;
    loader?: boolean;
    onSubmit: (formData, event, stringify, keepFormData, cleanedData) => void;
    onChange?: (formData) => void;
    onReset?: () => void;
    onRef?: any;
    readOnly?: boolean;
    disableEnterSubmit?: boolean;
    onRemove?: JSX.Element;
    backRoute?: string;
    backText?: string;
    moreView?: JSX.Element;
    submitHidden?: boolean;
    submitDisabled?: boolean;
    submitText?: string;
    actionBarHidden?: boolean;
    style?: React.CSSProperties;
}

const myRegexp = /\[(.*?)\]/g;

function EnhancedForm(props: FormProps) {
    const classes = useStyles();
    const {
        t,
        inputs,
        loader,
        payload,
        onChange,
        onReset,
        onSubmit,
        onRef,
        onRemove,
        backRoute,
        backText,
        moreView,
        disableEnterSubmit = true,
        readOnly,
        submitHidden,
        submitDisabled = false,
        submitText,
        actionBarHidden,
        style,
    } = props;

    const [initFormData, setInitFormData] = React.useState({});
    const [allComputeRules, setAllComputeRules] = React.useState<ComputeObject[]>([]);
    const [formData, setFormData] = React.useState(() => formatPayload());
    const [formHasError, setFormHasError] = React.useState(false);

    // TODO: use this code instead but bugging the form like new horo with reloading data
    // const [formData, setFormData] = React.useState({});
    // useEffect(() => {
    //     console.log('change payload');
    //     setFormData(formatPayload());
    // }, [payload]);

    useEffect(() => {
        if (onRef) {
            onRef({ setValue: setValue, setValues: setValues });
        }
        return () => {
            if (onRef) {
                onRef(undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRef]);

    useEffect(() => {
        setFormHasError(!!document.querySelector('form .MuiInputBase-root.Mui-error.MuiInputBase-formControl'));
    }, [formData]);

    // Format payload to add default value in options when only one option
    function formatPayload() {
        console.log('Initializing form data');
        if (!inputs || !Array.isArray(inputs)) {
            console.error('Inputs are not defined or not an array');
            return {};
        }
        inputs.forEach((row) => {
            const { datas, computeRules } = row;
            if (computeRules) {
                allComputeRules.push(...computeRules);
            }
            if (datas) {
                datas.forEach((data) => {
                    const match = myRegexp.exec(data.id);
                    if (data.defaultValue /*&& !ObjectUtils.getSubValueFromKey(payload, data.id)*/) {
                        ObjectUtils.setSubValueFromKey(payload, data.id, data.defaultValue);
                    } else if (data.options && Object.entries(data.options).length > 0) {
                        const multipleOptions: boolean = !data.optionsGroups && Object.entries(data.options).length > 1;
                        const valueAvailable = ObjectUtils.getSubValueFromKey(payload, data.id);
                        if (!multipleOptions || data.noEmpty) {
                            if (!valueAvailable || !data.options.find((o) => o.id === valueAvailable)) {
                                const defaultValue = ObjectUtils.getSubValueFromKey(
                                    data.options[0],
                                    match?.[1] ? match[1] : 'id',
                                );
                                ObjectUtils.setSubValueFromKey(payload, data.id, defaultValue);
                            }
                        }
                    }
                });
            }
        });
        setInitFormData(ObjectUtils.deepCopy(payload));
        console.log('Initialization done:', { payload, allComputeRules });
        return payload;
    }

    //=============================================//
    //=============== Event Handler ===============//
    //=============================================//

    const handleChange = (event, value?, shouldCallChange: boolean = true): void => {
        let nextState: any;
        if (value && (!event.target.type || event.target.autocomplete === 'off')) {
            const id = event.target.id.split('-option')[0];
            nextState = ObjectUtils.setSubValueFromKey({ ...formData }, id, value);
        } else if (value) {
            nextState = ObjectUtils.setSubValueFromKey({ ...formData }, event.target.name, value);
        } else {
            nextState = FormUtils.nextSubState(event, formData);
        }

        const rulesToCompute = allComputeRules.filter((r) => r.by === event.target.id);
        rulesToCompute.forEach((r) => {
            let isConditionValidated = true;
            for (const condition of r.conditions || []) {
                if (
                    condition.isBoolean !== null &&
                    ObjectUtils.getSubValueFromKey(nextState, condition.id) !== condition.isBoolean
                ) {
                    isConditionValidated = false;
                }
            }
            if (isConditionValidated) {
                const newVal = event.target.value;
                if (r.compute) {
                    const val = r.compute(nextState, newVal);
                    if (val && !isNaN(val)) {
                        ObjectUtils.setSubValueFromKey(nextState, r.target, val);
                    }
                }

                for (const hint of r.computeHint || []) {
                    for (const section of inputs) {
                        const data = section.datas.find((x) => x.id === hint.id);
                        if (data) {
                            const computed = hint.compute(nextState, newVal);
                            data.hint = computed ?? data.hint;
                            break;
                        }
                    }
                }
            }
        });

        setFormData(nextState);
        if (shouldCallChange && onChange) {
            onChange(nextState);
        }
    };

    const setValue = (name: string, value: any): void => {
        const nextState = ObjectUtils.setSubValueFromKey({ ...formData }, name, value);
        setFormData(nextState);
    };

    const setValues = (values: { name: string; value: any }[]): void => {
        let nextState = { ...formData };
        values.forEach((element) => {
            nextState = ObjectUtils.setSubValueFromKey(nextState, element.name, element.value);
        });
        setFormData(nextState);
    };

    const handleReset = (event): void => {
        // Reset all hints
        for (const section of inputs) {
            const hintsToReset = section.datas.filter((d) => d.hint);
            hintsToReset.forEach((d) => delete d.hint);
        }
        // Reset the datas
        setFormData(initFormData);
        if (onReset) {
            onReset();
        }
    };

    const handleSubmit = (event): void => {
        event.persist();
        event.preventDefault();
        const formated: any = trimObjectString(formData); // Trim all data in formData
        const stringify: any = new FormData(event.target);

        const inputKeys: InputDatas[] = inputs
            ? inputs.reduce((form, el) => {
                  return [...form, ...(el.datas ?? [])];
              }, [])
            : [];

        // Convert all date to timestamp into formatted key
        for (const entry of stringify.entries()) {
            const key = inputKeys.find((k) => k.id === entry[0]);
            if (key) {
                const { type, disableTimestampConvertion, dateOutputFormat } = key;

                if (!disableTimestampConvertion && (type === 'date' || type === 'datetime-local' || type === 'month')) {
                    const date = new Date(entry[1]);
                    if (date && date.toString() !== 'Invalid Date') {
                        if (dateOutputFormat) {
                            const formatedDate = DateUtils.dateFormat(date, dateOutputFormat);
                            ObjectUtils.setSubValueFromKey(formated, entry[0], formatedDate);
                        } else {
                            ObjectUtils.setSubValueFromKey(formated, entry[0], date.getTime() / 1000);
                        }
                    }
                }
                //TODO: add for phone
            }
        }

        // Remove from the submit all the hidden field that might have a value
        const cleanedData: any = Object.assign({}, formated);
        inputs.forEach((row) => {
            const { datas } = row;
            if (datas) {
                Object.keys(cleanedData).forEach((k) => {
                    const data = datas.find((d) => d.id === k);
                    if (data && !isVisible(data)) {
                        // console.log('removing not visible attribut', k);
                        delete cleanedData[k];
                    }
                });
            }
        });

        const keepFormData: any = Object.assign({}, formData);

        for (const entry of stringify.entries()) {
            if (!(entry[1] instanceof File)) {
                stringify.set(entry[0], JSON.stringify(entry[1]));
            } else {
                keepFormData[entry[0]] = '';
            }
        }

        onSubmit(formated, event, stringify, keepFormData, cleanedData);
    };

    const isVisible = (data: InputDatas): boolean => {
        if (!data) {
            return false;
        }

        let res: boolean = true;
        if (data.linked) {
            if (!(data.linked instanceof Array)) {
                data.linked = [data.linked];
            }
            for (const link of data.linked) {
                const { id, value } = link;
                const linkValue = ObjectUtils.getSubValueFromKey(formData, id);
                if (!Array.isArray(value)) {
                    if (linkValue === undefined && value === false) {
                        continue; // If it's a boolean we accept undefined as false
                    }
                    if (linkValue !== value) {
                        res = false;
                        break;
                    }
                } else {
                    res = value.find((v) => linkValue === v)?.length > 0;
                    if (!res) {
                        break;
                    }
                }
            }
        }
        return res;
    };

    // Reset is disabled for the moment
    const renderBottomActions = (): JSX.Element => {
        return (
            !actionBarHidden && (
                <FormControl margin="dense" className={classes.buttonContainer}>
                    {onRemove}
                    <div className={classes.buttonRight}>
                        {backRoute && (
                            <Button
                                variant="contained"
                                className={clsx(classes.rectangleButton, classes.buttonGrey)}
                                component={RouterLink}
                                to={backRoute}
                            >
                                {backText ?? t('common:cancel')}
                            </Button>
                        )}
                        {false && onReset && (
                            <Button
                                startIcon={<Icon>clear</Icon>}
                                variant="outlined"
                                className={clsx(
                                    classes.rectangleButton,
                                    !hasChanges ? classes.buttonGrey : classes.buttonRed,
                                )}
                                onClick={handleReset}
                            >
                                {t('common:reset')}
                            </Button>
                        )}
                        {(!submitHidden || loader) && (
                            <Button
                                startIcon={<Icon className={clsx(loader && classes.hidden)}>done</Icon>}
                                variant="contained"
                                type="submit"
                                disabled={loader || submitDisabled || formHasError} // problem with hasChanges => || !hasChanges
                                className={classes.rectangleButton}
                                color={!hasChanges || formHasError ? 'inherit' : 'secondary'}
                            >
                                {/* The label stays in the layout so the button keeps its width while saving */}
                                <span className={clsx(loader && classes.hidden)}>
                                    {submitText ?? t('common:validate')}
                                </span>
                                {loader && <CircularProgress size={20} className={classes.buttonLoader} />}
                            </Button>
                        )}
                    </div>
                </FormControl>
            )
        );
    };

    const hasChanges: boolean = !ObjectUtils.isEqual(formData, initFormData, true);
    // console.log({ formData, initFormData, hasChanges, formHasError });

    const form = (
        <div className={classes.formBlocks}>
            {inputs.map((row, index) => {
                const { title, subtitle, sectionStyle, datas, customView } = row;
                if (customView || !datas) {
                    return (
                        <div key={'section' + index} className={classes.section} style={sectionStyle}>
                            {customView}
                        </div>
                    );
                }
                return (
                    <div key={'section' + index} className={classes.section} style={sectionStyle}>
                        {title && (
                            <Typography variant="h6" component="h3" color="secondary">
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography variant="subtitle1" component="p" color="secondary">
                                {subtitle}
                            </Typography>
                        )}
                        {moreView}
                        <div className={classes.formInputs}>
                            {datas.map((data, i) => {
                                if (data.hidden || !isVisible(data)) {
                                    return null;
                                }

                                if (data.type === 'customButton') {
                                    return (
                                        <Button
                                            key={data.id}
                                            type="submit"
                                            variant="contained"
                                            startIcon={data.icon && <Icon>{data.icon}</Icon>}
                                            form={data.inputsAttributs?.form}
                                            onClick={() => {
                                                if (data.onClick) {
                                                    data.onClick(formData, setValues);
                                                }
                                            }}
                                        >
                                            {data.customButtonText || t('common:add')}
                                        </Button>
                                    );
                                }

                                const key: string = `form_${data.id}`;
                                const label: string = `${data.label} ${data.required ? '*' : ''}`;
                                const name: string = data.name ?? data.id;
                                let value: any = ObjectUtils.getSubValueFromKey(formData, name);
                                const disabled: boolean =
                                    data.disable ||
                                    (data.disabledBy && isVisible(datas.filter((x) => x.id === data.disabledBy.id)[0])
                                        ? ObjectUtils.getSubValueFromKey(formData, data.disabledBy.id) ===
                                          data.disabledBy.value
                                        : false);

                                const hint: JSX.Element = data.hint && (
                                    <div className={classes.formInput} style={{ marginTop: 10, color: 'grey' }}>
                                        {data.hint}
                                    </div>
                                );

                                const inputsProps: DefaultFormData = {
                                    id: data.id,
                                    name,
                                    label,
                                    hint,
                                    disabled,
                                    style: data.style,
                                    placeholder: data.placeholder,
                                    required: data.required,
                                    handleChange: data.onFieldChange ?? handleChange,
                                    regex: data.regex,
                                };

                                // For the customView
                                if (data.customView) {
                                    return (
                                        <div key={key} className={classes.flex}>
                                            {data.customView(value, handleChange, formData, hint)}
                                        </div>
                                    );
                                }

                                if (data.options || data.optionsGroups) {
                                    if (data.options && Object.entries(data.options).length === 0) {
                                        inputsProps.disabled = true;
                                    }
                                    const multipleOptions: boolean =
                                        !data.optionsGroups && Object.entries(data.options).length > 1;

                                    return (
                                        <FormOptions
                                            key={key}
                                            value={value}
                                            noEmpty={data.noEmpty}
                                            searchable={data.searchable}
                                            multipleOptions={multipleOptions}
                                            options={data.options}
                                            optionsGroups={data.optionsGroups}
                                            {...inputsProps}
                                        />
                                    );
                                } else if (data.boxes?.length > 0) {
                                    // Utilise l'id du champ pour stocker la sélection dans formData
                                    const selectedItems = formData[data.id] || [];
                                    const courseTypeView = data.boxes.map((item, key) => {
                                        const isSelected = selectedItems.some((s) => s.id === item.id);
                                        return (
                                            <FormControlLabel
                                                key={item.id}
                                                style={{ marginLeft: 8 }}
                                                control={
                                                    <Checkbox
                                                        checked={isSelected}
                                                        value={item.id}
                                                        onChange={() => {
                                                            let updatedSelected = Array.isArray(selectedItems)
                                                                ? [...selectedItems]
                                                                : [];
                                                            if (isSelected) {
                                                                updatedSelected = updatedSelected.filter(
                                                                    (s) => s.id !== item.id,
                                                                );
                                                            } else {
                                                                updatedSelected.push({ id: item.id, name: item.name });
                                                            }
                                                            setFormData({
                                                                ...formData,
                                                                [data.id]: updatedSelected,
                                                            });
                                                        }}
                                                    />
                                                }
                                                label={item.name}
                                            />
                                        );
                                    });
                                    return (
                                        <FormControl key={key} margin="dense">
                                            <div>{label}</div>
                                            <FormGroup row>{courseTypeView}</FormGroup>
                                        </FormControl>
                                    );
                                } else if (data.inputList?.length > 0) {
                                    return (
                                        <FormInputList
                                            key={key}
                                            value={value}
                                            inputList={data.inputList}
                                            {...inputsProps}
                                        />
                                    );
                                } else if (data.type === 'boolean') {
                                    const overrideProps = { ...inputsProps, ...data.inputsAttributs };
                                    return <FormBoolean key={key} value={value} {...overrideProps} />;
                                } else if (data.type === 'textarea') {
                                    return (
                                        <FormTextArea
                                            key={key}
                                            value={value}
                                            hint={hint}
                                            inputProps={data.inputsAttributs}
                                            {...inputsProps}
                                        />
                                    );
                                } else if (data.type === 'switch') {
                                    return (
                                        <FormSwitch
                                            key={key}
                                            value={value}
                                            switchOnLabel={data.switchOnLabel}
                                            switchTest={data.switchTest}
                                            {...inputsProps}
                                        />
                                    );
                                } else if (data.type === 'chips') {
                                    return (
                                        <FormChips
                                            key={key}
                                            value={value}
                                            options={data.chipsOptions}
                                            {...inputsProps}
                                        />
                                    );
                                } else {
                                    let inputProps: InputBaseComponentProps = {};
                                    if (value && data.type === 'date' && typeof value === 'number') {
                                        value = DateUtils.dateToString(new Date(value * 1000));
                                    }
                                    if (value && data.type === 'date' && typeof value === 'string') {
                                        value = value.split('T')[0];
                                    }

                                    if (data.linkDate) {
                                        // Can be an array of constraint
                                        const constraints = Array.isArray(data.linkDate)
                                            ? data.linkDate
                                            : [data.linkDate];

                                        constraints.forEach((constraint) => {
                                            const { next: isNextDate, isFullDate } = constraint;
                                            const linkDate = ObjectUtils.getSubValueFromKey(formData, constraint.id);
                                            let computedDate = DateUtils.dateFormat(
                                                (isNextDate
                                                    ? DateUtils.getNextDate(constraint.value, new Date(linkDate))
                                                    : new Date(linkDate)
                                                ).getTime(),
                                                'isoDateTime',
                                            ).split(isFullDate ? '+' : 'T')[0];
                                            if (isFullDate) {
                                                computedDate = computedDate.split(':').slice(0, 2).join(':');
                                            }
                                            inputProps[constraint.type] = computedDate;
                                        });
                                    }

                                    if (data.type === 'number') {
                                        inputProps.onWheel = (e) => {
                                            (e.target as HTMLElement).blur();
                                        };
                                    }

                                    if (data.type === 'number' && !data.numberNegative) {
                                        inputProps.min = 0;
                                        inputProps.step = 0.01;
                                    }
                                    if (data.type === 'number' && data.onlyNegative) {
                                        inputProps.max = 0;
                                        inputProps.step = 0.01;
                                    }
                                    if (data.type === 'file' || data.type === 'files') {
                                        inputProps.accept = ACCEPTED_UPLOAD_TYPE;
                                        if (data.type === 'files') {
                                            inputProps.multiple = true;
                                            data.type = 'file';
                                        }
                                    }
                                    if (data.type === 'tel' && !data.placeholder) {
                                        inputProps.placeholder = t('common:form.placeholder.phone');
                                    }
                                    inputProps = { ...inputProps, ...data.inputsAttributs };

                                    return (
                                        <FormInput
                                            key={key}
                                            type={data.type}
                                            inputProps={inputProps}
                                            startAdornment={data.startAdornment}
                                            endAdornment={data.endAdornment}
                                            value={value}
                                            hint={hint}
                                            onBlurOnly={data.onBlurOnly}
                                            format={data.format}
                                            {...inputsProps}
                                        />
                                    );
                                }
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const ElementWrapper = readOnly ? 'fieldset' : 'div';

    return (
        <div className={classes.rootForm} style={style}>
            <FormSubmit debounceSubmit={{ callback: handleSubmit }} disableEnterSubmit={disableEnterSubmit}>
                <ElementWrapper
                    className={readOnly ? classes.fieldset : undefined}
                    // style={readOnly ? { pointerEvents: 'none' } : undefined}
                    disabled={readOnly ? true : undefined}
                >
                    {form}
                    {renderBottomActions()}
                </ElementWrapper>
            </FormSubmit>
        </div>
    );
}

//=====================================//
//=============== Style ===============//
//=====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootForm: {
            width: '100%',
            marginTop: theme.spacing(3),
            paddingLeft: theme.spacing(12.5),
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(1),
                paddingLeft: theme.spacing(1),
            },
            '& .MuiFormControl-root.MuiTextField-root': {
                '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl[data-shrink=false]': {
                    whiteSpace: 'nowrap',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            },
        },
        formBlocks: {
            display: 'flex',
            flexWrap: 'wrap',
            minWidth: '50%',
            // alignItems: "center",
            flexDirection: 'row',
            justifyContent: 'start',
            '& .MuiFormControl-root': {
                margin: theme.spacing(1, 2),
            },
        },
        formInputs: {
            display: 'flex',
            flexWrap: 'wrap',
            minWidth: '50%',
            // alignItems: "center",
            flexDirection: 'column',
            justifyContent: 'start',
            marginTop: 30,
            '& .MuiFormControl-root': {
                margin: theme.spacing(1, 2),
            },
        },
        formInput: {
            width: '100%',
            minWidth: 200,
        },
        section: {
            width: '90%',
            [theme.breakpoints.up('lg')]: {
                width: '45%',
            },
            overflow: 'auto',
            minWidth: 500,
            marginTop: 40,
            marginRight: 40,
            padding: 50,
            [theme.breakpoints.down('sm')]: {
                marginTop: 20,
                marginRight: 20,
                padding: 30,
                minWidth: 'auto',
            },
            borderRadius: 14,
            background: theme.palette.colors.white,
            boxShadow: '0px 0px 42px 0px rgba(0, 0, 0, 0.06)',
        },
        buttonContainer: {
            display: 'flex',
            flexDirection: 'row',
            marginTop: 40,
        },
        buttonRight: {
            justifyContent: 'flex-end',
            flex: 1,
            display: 'flex',
            gap: 8,
        },
        roundButton: {
            padding: '16px 20px',
            borderRadius: 23,
            fontWeight: 'bold',
        },
        rectangleButton: {
            alignSelf: 'flex-end',
            padding: '6px 16px',
            textTransform: 'none',
        },
        hidden: {
            visibility: 'hidden',
        },
        buttonLoader: {
            position: 'absolute',
            top: 'calc(50% - 10px)',
            left: 'calc(50% - 10px)',
        },
        buttonGrey: {
            color: theme.palette.colors.greyDark,
        },
        buttonRed: {
            color: theme.palette.danger.contrastText,
        },
        flex: {
            flex: 1,
        },
        fieldset: {
            border: 0,
            margin: 0,
            padding: 0,
        },
    }),
);

export default withTranslation(['form', 'common'])(EnhancedForm);
